import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Sparkles, Mail, X, Trash2, Check, ArrowRight, Send } from "lucide-react";
import { toast } from "sonner";
import Markdown from "../components/Markdown";

const CAMPAIGN_TYPES = [
  { id: "newsletter_educativa", label: "📚 Newsletter educativa" },
  { id: "sabias_que", label: "🤔 ¿Sabías que...?" },
  { id: "storytelling", label: "📖 Storytelling de marca" },
  { id: "promo", label: "🏷️ Promoción" },
  { id: "lanzamiento", label: "🚀 Lanzamiento producto" },
  { id: "fidelizacion", label: "💎 Fidelización" },
];

const STAGES = [
  { id: "frio", label: "🔵 Frío (no nos conoce)" },
  { id: "tibio", label: "🟡 Tibio (conoce, no compra)" },
  { id: "caliente", label: "🔴 Caliente (listo)" },
  { id: "fidelizacion", label: "💎 Fidelizado (ya compró)" },
];

const OBJECTIVES = [
  { id: "informar", label: "Informar" },
  { id: "educar", label: "Educar" },
  { id: "vender", label: "Vender" },
  { id: "fidelizar", label: "Fidelizar" },
];

const STATUS_COLORS = {
  programado: "pill-blue",
  enviado: "pill-green",
  cancelado: "pill-gray",
};

const EmailMarketing = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/marketing/email").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const markSent = async (id) => {
    if (!window.confirm("¿Marcar email como enviado?")) return;
    await api.post(`/marketing/email/${id}/mark-sent`);
    toast.success("Email marcado como enviado");
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar email?")) return;
    await api.delete(`/marketing/email/${id}`);
    load();
  };

  return (
    <Layout
      title="Email Marketing"
      subtitle="Línea editorial · convierte frío en caliente sin spammear"
      actions={
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-email-btn">
          <Sparkles size={14} /> Generar emails
        </button>
      }
    >
      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : items.length === 0 ? (
        <div className="aura-card-flat p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#D99D26]/15 text-[#8a5e10] flex items-center justify-center mx-auto mb-4">
            <Mail size={24} />
          </div>
          <h3 className="font-display text-2xl font-medium">Sin emails programados</h3>
          <p className="text-[#666] mt-2 mb-5 max-w-md mx-auto">Define la etapa de tu audiencia y el tipo de campaña. Aura escribe 3 propuestas siguiendo línea editorial estratégica (educativa + storytelling, no solo promo).</p>
          <button onClick={() => setShowForm(true)} className="btn-primary">Crear primer email</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((e) => (
            <div key={e.id} className="aura-card p-5 cursor-pointer group relative" onClick={() => setSelected(e)} data-testid={`email-${e.id}`}>
              <div className="flex justify-between items-start mb-2">
                <span className={`pill ${STATUS_COLORS[e.status]} text-[10px]`}>{e.status}</span>
                <span className="pill pill-gray text-[10px]">{e.audience_segment}</span>
              </div>
              <h3 className="font-display text-base font-medium leading-tight">{e.subject}</h3>
              {e.preheader && <p className="text-xs text-[#666] mt-1 line-clamp-1">{e.preheader}</p>}
              <div className="text-xs text-[#666] mt-3 flex justify-between">
                <span>{e.target_date} · {e.target_time}</span>
                {e.assignee_name && <span>{e.assignee_name}</span>}
              </div>
              <div className="flex justify-end gap-1 mt-3 pt-3 border-t border-[#E5E5E5]">
                {e.status === "programado" && (
                  <button onClick={(ev) => { ev.stopPropagation(); markSent(e.id); }} className="text-xs text-[#8a5e10] font-medium hover:text-[#0A0A0A] flex items-center gap-1" data-testid={`mark-sent-email-${e.id}`}>
                    <Send size={11} /> Marcar enviado
                  </button>
                )}
                <button onClick={(ev) => { ev.stopPropagation(); remove(e.id); }} className="text-[#666] hover:text-red-600 ml-2"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && <EmailGeneratorFlow onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      {selected && <EmailDetail email={selected} onClose={() => setSelected(null)} />}
    </Layout>
  );
};

const EmailGeneratorFlow = ({ onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    campaign_type: "sabias_que",
    audience_stage: "frio",
    objective: "educar",
    month_context: "",
    products: "",
    extra_notes: "",
    target_date: new Date().toISOString().slice(0, 10),
    target_time: "10:00",
  });
  const [generating, setGenerating] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/marketing/email/generate", {
        ...form,
        products: form.products.split(",").map((s) => s.trim()).filter(Boolean),
        num_proposals: 3,
      });
      setProposals(res.data.proposals || []);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setGenerating(false); }
  };

  const pickProposal = (p) => {
    setEditing({ ...p });
    setStep(3);
  };

  const approve = async () => {
    setSaving(true);
    try {
      await api.post("/marketing/email/approve", {
        ...editing,
        target_date: form.target_date,
        target_time: form.target_time,
      });
      toast.success("Email aprobado · agregado al calendario y notificado a Samantha");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto p-7">
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="label-overline">Aura · Email Marketing</div>
            <h2 className="font-display text-2xl font-medium">
              {step === 1 ? "Estrategia editorial" : step === 2 ? "Elige tu propuesta" : "Edita y aprueba"}
            </h2>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="flex gap-2 mb-6">
          {[1, 2, 3].map((s) => <div key={s} className={`flex-1 h-1.5 rounded-full ${step >= s ? "bg-[#D99D26]" : "bg-[#F0F0F0]"}`} />)}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="aura-label">Tipo de campaña *</label>
                <select className="aura-input" value={form.campaign_type} onChange={(e) => setForm({ ...form, campaign_type: e.target.value })} data-testid="email-campaign-type">
                  {CAMPAIGN_TYPES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="aura-label">Etapa de la audiencia *</label>
                <select className="aura-input" value={form.audience_stage} onChange={(e) => setForm({ ...form, audience_stage: e.target.value })} data-testid="email-audience-stage">
                  {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="aura-label">Objetivo</label>
                <select className="aura-input" value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })}>
                  {OBJECTIVES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="aura-label">Fecha tentativa de envío</label>
                <input type="date" className="aura-input" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} />
              </div>
              <div>
                <label className="aura-label">Hora</label>
                <input type="time" className="aura-input" value={form.target_time} onChange={(e) => setForm({ ...form, target_time: e.target.value })} />
              </div>
              <div>
                <label className="aura-label">Productos a mencionar</label>
                <input className="aura-input" value={form.products} onChange={(e) => setForm({ ...form, products: e.target.value })} placeholder="212 Men, Sauvage, perfume árabe X" />
              </div>
              <div className="col-span-2">
                <label className="aura-label">Contexto del mes</label>
                <textarea className="aura-input" rows="2" value={form.month_context} onChange={(e) => setForm({ ...form, month_context: e.target.value })} placeholder="4x3 + Día del Padre + Mundial + nueva colección árabe" />
              </div>
              <div className="col-span-2">
                <label className="aura-label">Notas extras / ángulo</label>
                <input className="aura-input" value={form.extra_notes} onChange={(e) => setForm({ ...form, extra_notes: e.target.value })} placeholder="Que se sienta cercano y mexicano..." />
              </div>
            </div>
            <button onClick={generate} disabled={generating} className="btn-primary w-full justify-center flex items-center gap-2" data-testid="generate-email-btn">
              {generating ? <><span className="spinner" /> Aura escribiendo propuestas...</> : <><Sparkles size={14} /> Generar 3 emails</>}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <p className="text-sm text-[#666]">{proposals.length} propuestas. Click en la que más te guste.</p>
            <div className="space-y-3">
              {proposals.map((p, i) => (
                <div key={p.id} onClick={() => pickProposal(p)} className="aura-card p-5 cursor-pointer hover:border-[#D99D26] hover:shadow-md transition-all" data-testid={`email-proposal-${i}`}>
                  <div className="flex justify-between mb-3">
                    <span className="pill pill-yellow text-[10px]">Email {i + 1}</span>
                    <ArrowRight size={12} className="text-[#8a5e10]" />
                  </div>
                  <div className="bg-[#FAFAFA] rounded-lg p-3 mb-2 border border-[#E5E5E5]">
                    <div className="label-overline text-[9px]">Asunto</div>
                    <div className="text-sm font-medium">{p.subject}</div>
                    {p.preheader && <div className="text-xs text-[#666] mt-1">{p.preheader}</div>}
                  </div>
                  <div className="text-xs text-[#666] line-clamp-3 markdown-content">
                    <Markdown content={p.body_markdown?.slice(0, 300) || ""} />
                  </div>
                  {p.rationale && <div className="text-[11px] italic mt-2 text-[#8a5e10]">💡 {p.rationale}</div>}
                </div>
              ))}
            </div>
            <button onClick={() => setStep(1)} className="btn-ghost">← Volver</button>
          </div>
        )}

        {step === 3 && editing && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="aura-label">Asunto * ({editing.subject?.length || 0}/60)</label>
                <input className="aura-input" maxLength={80} value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} data-testid="email-edit-subject" />
              </div>
              <div className="col-span-2">
                <label className="aura-label">Preheader</label>
                <input className="aura-input" maxLength={120} value={editing.preheader} onChange={(e) => setEditing({ ...editing, preheader: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="aura-label">Título dentro del email</label>
                <input className="aura-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="aura-label">Cuerpo (markdown)</label>
                <textarea className="aura-input font-mono text-sm" rows="12" value={editing.body_markdown} onChange={(e) => setEditing({ ...editing, body_markdown: e.target.value })} data-testid="email-edit-body" />
              </div>
              <div>
                <label className="aura-label">CTA</label>
                <input className="aura-input" value={editing.cta} onChange={(e) => setEditing({ ...editing, cta: e.target.value })} placeholder="Descubre en la app" />
              </div>
              <div>
                <label className="aura-label">Segmento audiencia</label>
                <input className="aura-input" value={editing.audience_segment} onChange={(e) => setEditing({ ...editing, audience_segment: e.target.value })} />
              </div>
            </div>
            <div className="aura-card-flat p-4 bg-[#FFF9E6]/40 text-sm text-[#666]">
              <strong className="text-[#0A0A0A]">Al aprobar:</strong> el email se asigna a <strong>Samantha</strong> y aparece en el calendario editorial para {form.target_date} {form.target_time}.
            </div>
            <div className="flex justify-between gap-2">
              <button onClick={() => setStep(2)} className="btn-ghost">← Cambiar propuesta</button>
              <button onClick={approve} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="approve-email-btn">
                {saving ? <><span className="spinner" /> Guardando...</> : <><Check size={14} /> Aprobar y agregar al calendario</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const EmailDetail = ({ email, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-7">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="label-overline">Asunto</div>
            <h2 className="font-display text-xl font-medium">{email.subject}</h2>
            {email.preheader && <p className="text-sm text-[#666] mt-1">{email.preheader}</p>}
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        {email.title && <h3 className="font-display text-2xl mt-5 mb-3">{email.title}</h3>}
        <div className="aura-card-flat p-5 markdown-content">
          <Markdown content={email.body_markdown || ""} />
        </div>
        {email.cta && (
          <div className="mt-5 text-center">
            <button className="btn-primary px-8">{email.cta}</button>
          </div>
        )}
        <div className="border-t border-[#E5E5E5] mt-5 pt-4 text-xs text-[#666] flex justify-between">
          <span>Segmento: {email.audience_segment}</span>
          <span>{email.target_date} · {email.target_time}</span>
        </div>
      </div>
    </div>
  );
};

export default EmailMarketing;
